// src/ManageUsers.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import barangaysData from './barangays.json';
import { useNavigate } from 'react-router-dom';
import { FaPen, FaTrash, FaClock, FaPlus, FaUpload } from 'react-icons/fa6';
import Preloader from './Preloader'; 
import Select from 'react-select';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fadeCellStyle = {
  maxWidth: 120,
  whiteSpace: 'nowrap', 
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'middle',
  position: 'relative',
};

// Define action categories and actions
const ACTION_CATEGORIES = [
  {
    label: 'Announcements',
    options: [
      { value: 'post announcement', label: 'Post Announcement' },
      { value: 'edited announcement', label: 'Edited Announcement' },
      { value: 'archived announcement', label: 'Archived Announcement' },
    ],
  },
  {
    label: 'Incident Reports',
    options: [
      { value: 'created incident report', label: 'Created Incident Report' },
      { value: 'edited incident report', label: 'Edited Incident Report' },
      { value: 'archived incident report', label: 'Archived Incident Report' },
      { value: 'downvoted incident report', label: 'Downvoted Incident Report' },
      { value: 'upvote incident report', label: 'Upvote Incident Report' },
    ],
  },
  {
    label: 'Missing Reports',
    options: [
      { value: 'created missing report', label: 'Created Missing Report' },
      { value: 'edited  missing report', label: 'Edited Missing Report' },
      { value: 'archived missing report', label: 'Archived Missing Report' },
    ],
  },
  {
    label: 'Evacuation Shelters',
    options: [
      { value: 'added evacuation shelter', label: 'Added Evacuation Shelter' },
      { value: 'edited  evacuation shelter', label: 'Edited Evacuation Shelter' },
      { value: 'archived evacuation shelter', label: 'Archived Evacuation Shelter' },
    ],
  },
  {
    label: 'Evacuees',
    options: [
      { value: 'added evacuee', label: 'Added Evacuee' },
      { value: 'edited evacuee', label: 'Edited Evacuee' },
      { value: 'archived evacuee', label: 'Archived Evacuee' },
      { value: 'checkin evacuee', label: 'Checkin Evacuee' },
      { value: 'checkout evacuee', label: 'Checkout Evacuee' },
      { value: 'evacuation print logs', label: 'Evacuation Print Logs' },
    ],
  },
  {
    label: 'Temporary Shelters',
    options: [
      { value: 'added temporary shelter', label: 'Added Temporary Shelter' },
      { value: 'edited temporary shelter', label: 'Edited Temporary Shelter' },
      { value: 'accepted temporary shelter', label: 'Accepted Temporary Shelter' },
      { value: 'rejected temporary shelter', label: 'Rejected Temporary Shelter' },
    ],
  },
  {
    label: 'Households',
    options: [
      { value: 'view temporary shelter', label: 'View Temporary Shelter' },
      { value: 'edit household', label: 'Edit Household' },
    ],
  },
  {
    label: 'SOS',
    options: [
      { value: 'view sos beacon', label: 'View SOS Beacon' },
      { value: 'sent message', label: 'Sent Message' },
    ],
  },
  {
    label: 'Users',
    options: [
      { value: 'created user', label: 'Created User' },
      { value: 'edited user', label: 'Edited User' },
      { value: 'archived user', label: 'Archived User' },
      { value: 'banned user', label: 'Banned User' },
      { value: 'unbanned user', label: 'Unbanned User' },
    ],
  },
  {
    label: 'ID Verification',
    options: [
      { value: 'view ID', label: 'View ID' },
      { value: 'accepted ID', label: 'Accepted ID' },
      { value: 'rejected ID', label: 'Rejected ID' },
      { value: 'reevaluated ID', label: 'Reevaluated ID' },
    ],
  },
  {
    label: 'Authentication',
    options: [
      { value: 'user login', label: 'User Login' },
      { value: 'user logout', label: 'User Logout' },
      { value: 'logout for banned', label: 'Logout for Banned' },
      { value: 'logout by unauthorized', label: 'Logout by Unauthorized' },
    ],
  },
];

// Helper to flatten all actions for quick lookup
const ALL_ACTIONS = ACTION_CATEGORIES.flatMap(cat => cat.options);

// Helper to get all actions in a category
const getActionsForCategory = (categoryLabel) => {
  const cat = ACTION_CATEGORIES.find(c => c.label === categoryLabel);
  return cat ? cat.options : [];
};

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
    birth_date: '',
    gender: '',
    house_street: '',
    subdivision: '',
    barangay: '',
    city: 'Legazpi City',
    province: 'Albay',
    zip: '4500',
    status: 'active',
    role: 'resident',
    avatar_url: '',
    id_document_url: '',
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState(null);
  const [banRecords, setBanRecords] = useState({});
  const [showBanMenu, setShowBanMenu] = useState(false);
  const [showUnbanMenu, setShowUnbanMenu] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [banForm, setBanForm] = useState({
    duration: '1h',
    customHours: '',
    customMinutes: '',
    reason: ''
  });
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(users.length / itemsPerPage);
  const paginatedUsers = users.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditUsers, setAuditUsers] = useState([]);
  const [auditUserFilter, setAuditUserFilter] = useState([]);
  const [auditActionFilter, setAuditActionFilter] = useState([]); // array of selected actions or categories
  const [auditDateRange, setAuditDateRange] = useState({ from: '', to: '' });
  const [auditFilteredLogs, setAuditFilteredLogs] = useState([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);

  useEffect(() => {
      document.title = "Manage Users";
    }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      } else {
        navigate('/login');
      }
    };
    getCurrentUser();
  }, [navigate]);

  const fetchUsers = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    setLoading(true);
    try {
      // First, call the unban function to update expired bans
      try {
        await supabase.rpc('unban_expired_users');
        console.log('Unban expired users function executed');
      } catch (error) {
        console.log('Unban function not available yet:', error.message);
      }

      let query = supabase.from('profiles').select('*');
      
      if (currentUser.role?.trim() !== 'super admin') {
        query = query.eq('id', currentUser.id);
      } else {
        if (search) {
          query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`);
        }
        if (filter) {
          query = query.eq('role', filter);
        }
        if (statusFilter) {
          query = query.eq('status', statusFilter);
        }
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } else {
        setUsers(data || []);
      }
    } catch (error) {
      console.error('Error in fetchUsers:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser, search, filter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchBanRecords();
  }, []);

  useEffect(() => {
    const profilesChannel = supabase
      .channel('public:profiles_page')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' },
        (payload) => {
          console.log('Profiles change received!', payload);
          fetchUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(profilesChannel);
    };
  }, [fetchUsers]);

  function openForm(user = null) {
    if (currentUser.role?.trim() !== 'super admin' && user && user.id !== currentUser.id) {
      alert("You are not authorized to edit this user.");
      return;
    }
    if (currentUser.role?.trim() !== 'super admin' && !user) {
      alert("You are not authorized to create a new user.");
      return;
    }

    if (user) {
      setForm({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        mobile_number: user.mobile_number,
        birth_date: user.birth_date,
        gender: user.gender,
        house_street: user.house_street,
        subdivision: user.subdivision,
        barangay: user.barangay,
        city: user.city,
        province: user.province,
        zip: user.zip,
        status: user.status,
        role: user.role || 'resident',
        avatar_url: user.avatar_url || '',
        id_document_url: user.id_document_url || '',
      });
      setEditId(user.id);
    } else {
      setForm({
        first_name: '',
        last_name: '',
        email: '',
        mobile_number: '',
        birth_date: '',
        gender: '',
        house_street: '',
        subdivision: '',
        barangay: '',
        city: 'Legazpi City',
        province: 'Albay',
        zip: '4500',
        status: 'active',
        role: 'resident',
        avatar_url: '',
        id_document_url: '',
      });
      setEditId(null);
    }
    setShowForm(true);
  }

  async function handleImageUpload(event) {
    const file = event.target.files[0];
    if (file) {
      try {
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        setForm(prev => ({ ...prev, avatar_url: publicUrl }));

      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    }
  }

  async function handleIdImageUpload(event) {
    const file = event.target.files[0];
    if (file) {
      try {
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;
        const filePath = `ids/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('id-documents')
          .upload(filePath, file);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage
          .from('id-documents')
          .getPublicUrl(filePath);
        setForm(prev => {
          let newStatus = prev.id_verification_status;
          if ((prev.id_verification_status === 'pending' || !prev.id_verification_status) && publicUrl) {
            newStatus = 'submitted';
          }
          return { ...prev, id_document_url: publicUrl, id_verification_status: newStatus };
        });
      } catch (error) {
        console.error('Error uploading ID image:', error);
        alert('Failed to upload ID image. Please try again.');
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prevent non-super admins from changing roles
      if (currentUser.role?.trim() !== 'super admin' && editId && editId !== currentUser.id) {
        alert("You are not authorized to edit this user's role.");
        return;
      }
      
      // Ensure id_verification_status is set to 'submitted' if id_document_url is present and status is pending
      let formToSave = { ...form };
      if (formToSave.id_document_url && (formToSave.id_verification_status === 'pending' || !formToSave.id_verification_status)) {
        formToSave.id_verification_status = 'submitted';
      }
      
      if (editId) {
        // Update existing user
        const { error: updateError } = await supabase
          .from('profiles')
          .update(formToSave)
          .eq('id', editId);
        
        if (updateError) throw updateError;
        // Audit log: user edit
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'edited user',
          target_id: editId,
          target_type: 'User',
          details: { email: formToSave.email, name: `${formToSave.first_name} ${formToSave.last_name}` }
        }]);
      } else {
        // Create new user
        console.log('Starting user creation process...');
        console.log('Form data:', formToSave);
        
        // Step 1: Check if email already exists in profiles table
        console.log('Checking if email exists in profiles table...');
        const { data: existingProfile, error: checkProfileError } = await supabase
          .from('profiles')
          .select('email, user_id, id')
          .eq('email', formToSave.email)
          .single();

        console.log('Profile check result:', { existingProfile, checkProfileError });

        if (checkProfileError && checkProfileError.code !== 'PGRST116') {
          console.error('Error checking existing profile:', checkProfileError);
          throw checkProfileError;
        }

        if (existingProfile) {
          console.log('Email already exists in profiles table:', existingProfile);
          throw new Error('A user with this email already exists in the system');
        }

        // Step 2: Create auth user
        console.log('Creating auth user...');
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formToSave.email,
          password: 'temporaryPassword123!',
        });
        
        console.log('Auth creation result:', { authData, authError });
        
        if (authError) {
          console.error('Auth creation failed:', authError);
          if (authError.message.includes('already registered') || 
              authError.message.includes('already exists') ||
              authError.message.includes('duplicate')) {
            throw new Error('A user with this email is already registered');
          }
          throw authError;
        }
        
        if (!authData.user) {
          console.error('No user returned from auth creation');
          throw new Error('Failed to create authentication user');
        }
        
        console.log('Auth user created successfully:', authData.user.id);
        
        // Step 3: Check if a profile was automatically created (by trigger/RLS)
        console.log('Checking if profile was automatically created...');
        const { data: autoCreatedProfile, error: autoProfileError } = await supabase
          .from('profiles')
          .select('id, email, user_id')
          .eq('user_id', authData.user.id)
          .single();
        
        console.log('Auto-created profile check result:', { autoCreatedProfile, autoProfileError });
        
        // Step 4: Insert or update profile
        console.log('Inserting/updating profile...');
        const profileData = {
          ...formToSave,
          user_id: authData.user.id
        };
        console.log('Profile data to insert/update:', profileData);
        
        let insertError;
        if (autoCreatedProfile) {
          // Profile was automatically created, so update it
          console.log('Profile already exists, updating...');
          const { error: updateError } = await supabase
            .from('profiles')
            .update(profileData)
            .eq('id', autoCreatedProfile.id);
          insertError = updateError;
        } else {
          // No profile exists, so insert new one
          console.log('No profile exists, inserting new one...');
          const { error: insertNewError } = await supabase
            .from('profiles')
            .insert([profileData]);
          insertError = insertNewError;
        }
        
        console.log('Profile operation result:', { insertError });
        
        if (insertError) {
          console.error('Profile operation failed:', insertError);
          console.error('Error details:', {
            message: insertError.message,
            details: insertError.details,
            hint: insertError.hint,
            code: insertError.code
          });
          
          // Handle specific constraint violations
          if (insertError.code === '23505') {
            if (insertError.message.includes('unique_user')) {
              // This means either email or user_id already exists
              // Let's check which one
              const { data: emailCheck } = await supabase
                .from('profiles')
                .select('id')
                .eq('email', formToSave.email)
                .single();
              
              const { data: userIdCheck } = await supabase
                .from('profiles')
                .select('id')
                .eq('user_id', authData.user.id)
                .single();
              
              if (emailCheck) {
                throw new Error('A user with this email already exists in the system');
              } else if (userIdCheck) {
                throw new Error('User ID conflict detected. Please try again.');
              } else {
                throw new Error('Duplicate entry detected. Please check your input.');
              }
            }
          }
          
          throw insertError;
        }
        
        console.log('Profile created successfully');
        // Audit log: user creation
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id, // The admin who created the user
          action: 'created user',
          target_id: authData.user.id, // The new user's ID
          target_type: 'User',
          details: { email: formToSave.email, name: `${formToSave.first_name} ${formToSave.last_name}` }
        }]);
      }

      setShowForm(false);
      await fetchUsers();
      if (editId) {
        alert('User updated successfully!');
      } else {
        alert('User created successfully!\nDefault password is: temporaryPassword123!');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      console.error('Full error object:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to save user';
      
      if (error.code === '23505') {
        if (error.message.includes('unique_user')) {
          errorMessage = 'A user with this email already exists in the system';
        } else if (error.message.includes('user_id')) {
          errorMessage = 'User ID conflict. Please try again.';
        } else if (error.message.includes('email')) {
          errorMessage = 'A user with this email already exists.';
        } else {
          errorMessage = 'Duplicate entry detected. Please check your input.';
        }
      } else if (error.message.includes('already exists') || 
                 error.message.includes('already registered') ||
                 error.message.includes('duplicate')) {
        errorMessage = error.message;
      } else if (error.message.includes('Failed to create authentication user')) {
        errorMessage = 'Failed to create user account. Please try again.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(id, newStatus) {
    if (!currentUser) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: newStatus })
        .eq('id', id);
      
      if (error) throw error;
      await fetchUsers();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  }

  async function handleArchive(id) {
    if (!currentUser) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: 'archived' })
        .eq('id', id);
      
      if (error) throw error;
      // Audit log: user archive
      await supabase.from('audit_logs').insert([{
        user_id: currentUser.id,
        action: 'archived user',
        target_id: id,
        target_type: 'User',
        details: {}
      }]);
      await fetchUsers();
    } catch (error) {
      console.error('Error archiving user:', error);
      alert(`Failed to archive user: ${error.message}`);
    }
  }

  // Ban/Unban functions
  const openBanMenu = (userId) => {
    setSelectedUserId(userId);
    setBanForm({
      duration: '1h',
      customHours: '',
      customMinutes: '',
      reason: ''
    });
    setShowBanMenu(true);
  };

  const openUnbanMenu = (userId) => {
    setSelectedUserId(userId);
    setBanForm({
      duration: '1h',
      customHours: '',
      customMinutes: '',
      reason: ''
    });
    setShowUnbanMenu(true);
  };

  const calculateBanDuration = (duration, customHours, customMinutes) => {
    const now = new Date();
    let endDate;

    switch (duration) {
      case '1h':
        endDate = new Date(now.getTime() + 60 * 60 * 1000);
        break;
      case '24h':
        endDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        break;
      case '7d':
        endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case 'permanent':
        endDate = new Date(now.getTime() + 100 * 365 * 24 * 60 * 60 * 1000); // 100 years
        break;
      case 'custom':
        const hours = parseInt(customHours) || 0;
        const minutes = parseInt(customMinutes) || 0;
        endDate = new Date(now.getTime() + (hours * 60 + minutes) * 60 * 1000);
        break;
      default:
        endDate = new Date(now.getTime() + 60 * 60 * 1000); // Default 1 hour
    }

    return endDate.toISOString();
  };

  const handleBanUser = async () => {
    if (!selectedUserId || !banForm.reason.trim()) {
      alert('Please provide a reason for the ban.');
      return;
    }

    try {
      const banEndDate = calculateBanDuration(banForm.duration, banForm.customHours, banForm.customMinutes);
      
      console.log('Banning user:', selectedUserId);
      console.log('Ban end date:', banEndDate);
      
      // Insert ban record
      const { error: banError } = await supabase
        .from('user_bans')
        .insert([{
          user_id: selectedUserId,
          banned_by: currentUser.id,
          reason: banForm.reason,
          ban_start: new Date().toISOString(),
          ban_end: banEndDate,
          is_active: true
        }]);

      if (banError) {
        console.error('Ban record insert error:', banError);
        throw banError;
      }

      console.log('Ban record inserted successfully');

      // Update user status to banned
      const { error: statusError } = await supabase
        .from('profiles')
        .update({ status: 'banned' })
        .eq('id', selectedUserId);

      if (statusError) {
        console.error('Profile status update error:', statusError);
        throw statusError;
      }

      console.log('Profile status updated to banned successfully');
      // Audit log: user ban
      await supabase.from('audit_logs').insert([{
        user_id: currentUser.id,
        action: 'banned user',
        target_id: selectedUserId,
        target_type: 'User',
        details: { reason: banForm.reason, ban_end: banEndDate }
      }]);

      setShowBanMenu(false);
      await fetchUsers();
      await fetchBanRecords();
      alert('User has been banned successfully.');
    } catch (error) {
      console.error('Error banning user:', error);
      alert(`Failed to ban user: ${error.message}`);
    }
  };

  const handleUnbanUser = async () => {
    if (!selectedUserId) return;

    try {
      let banEndDate;
      
      if (banForm.duration === 'immediate') {
        banEndDate = new Date().toISOString();
      } else {
        banEndDate = calculateBanDuration(banForm.duration, banForm.customHours, banForm.customMinutes);
      }

      // Get current ban_end for the user
      const currentBan = banRecords[selectedUserId];
      const currentBanEnd = currentBan ? new Date(currentBan.ban_end) : null;
      const newBanEnd = new Date(banEndDate);
      const now = new Date();
      let unbanType = 'reduced';
      let userBanUpdate;
      let userStatusUpdate;

      // Only allow reducing ban duration
      if (currentBanEnd && newBanEnd > currentBanEnd) {
        alert('You can only reduce the ban duration. The new unban time must be earlier than the current ban end.');
        return;
      }

      if (newBanEnd <= now) {
        // Unban immediately
        unbanType = 'immediate';
        userBanUpdate = {
          ban_end: banEndDate,
          is_active: false,
          unbanned_by: currentUser.id,
          unban_reason: banForm.reason || 'Manually unbanned'
        };
        userStatusUpdate = { status: 'active' };
      } else {
        // Reduce ban duration, keep banned
        userBanUpdate = {
          ban_end: banEndDate,
          unbanned_by: currentUser.id,
          unban_reason: banForm.reason || 'Ban duration reduced'
        };
        userStatusUpdate = { status: 'banned' };
      }

      // Update ban record
      const { error: banError } = await supabase
        .from('user_bans')
        .update(userBanUpdate)
        .eq('user_id', selectedUserId)
        .eq('is_active', true);
      if (banError) throw banError;

      // Update user status if needed
      const { error: statusError } = await supabase
        .from('profiles')
        .update(userStatusUpdate)
        .eq('id', selectedUserId);
      if (statusError) throw statusError;

      // Audit log: user unban
      await supabase.from('audit_logs').insert([{
        user_id: currentUser.id,
        action: 'unbanned user',
        target_id: selectedUserId,
        target_type: 'User',
        details: {
          unban_type: unbanType,
          new_ban_end: banEndDate,
          reason: banForm.reason || (unbanType === 'immediate' ? 'Manually unbanned' : 'Ban duration reduced')
        }
      }]);

      setShowUnbanMenu(false);
      await fetchUsers();
      await fetchBanRecords();
      alert('User has been unbanned successfully.');
    } catch (error) {
      console.error('Error unbanning user:', error);
      alert(`Failed to unban user: ${error.message}`);
    }
  };

  const fetchBanRecords = async () => {
    try {
      console.log('Fetching ban records...');
      const { data, error } = await supabase
        .from('user_bans')
        .select('*')
        .eq('is_active', true);

      console.log('Ban records response:', { data, error });

      if (error) {
        console.error('Error fetching ban records:', error);
        // If table doesn't exist, just return empty object
        if (error.code === '42P01') { // Table doesn't exist
          console.log('user_bans table does not exist yet');
          setBanRecords({});
          return;
        }
        throw error;
      }

      const banMap = {};
      data.forEach(ban => {
        banMap[ban.user_id] = ban;
      });
      console.log('Ban map created:', banMap);
      setBanRecords(banMap);
    } catch (error) {
      console.error('Error in fetchBanRecords:', error);
      setBanRecords({});
    }
  };

  const isUserBanned = (userId) => {
    const banRecord = banRecords[userId];
    console.log(`Checking if user ${userId} is banned:`, { banRecord, banRecords });
    
    if (!banRecord) {
      console.log(`No ban record found for user ${userId}`);
      return false;
    }
    
    const now = new Date();
    const banEnd = new Date(banRecord.ban_end);
    const isBanned = now < banEnd && banRecord.is_active;
    
    console.log(`User ${userId} ban status:`, { 
      now: now.toISOString(), 
      banEnd: banEnd.toISOString(), 
      isActive: banRecord.is_active,
      isBanned 
    });
    
    return isBanned;
  };

  const getBanInfo = (userId) => {
    const banRecord = banRecords[userId];
    if (!banRecord) return null;
    
    const now = new Date();
    const banEnd = new Date(banRecord.ban_end);
    const timeLeft = banEnd - now;
    
    if (timeLeft <= 0) return null;
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    return {
      reason: banRecord.reason,
      timeLeft: `${hours}h ${minutes}m`,
      banEnd: banEnd.toLocaleString()
    };
  };

  // Add this debug function after the existing functions
  const debugProfilesTable = async () => {
    try {
      console.log('=== DEBUGGING PROFILES TABLE ===');
      
      // Try to get table info
      const { data: tableInfo, error: tableError } = await supabase
        .from('profiles')
        .select('*')
        .limit(1);
      
      console.log('Table info:', tableInfo);
      console.log('Table error:', tableError);
      
      // Check for existing users with the same email
      const { data: existingUsers, error: existingError } = await supabase
        .from('profiles')
        .select('email, user_id, id')
        .eq('email', form.email);
      
      console.log('Existing users with same email:', existingUsers);
      console.log('Existing users error:', existingError);
      
      // Check for existing users with the same user_id (if we have one)
      if (form.user_id) {
        const { data: existingUserIds, error: userIdError } = await supabase
          .from('profiles')
          .select('email, user_id, id')
          .eq('user_id', form.user_id);
        
        console.log('Existing users with same user_id:', existingUserIds);
        console.log('User ID check error:', userIdError);
      }
      
    } catch (error) {
      console.error('Debug error:', error);
    }
  };

  // Add this to your form submission to debug
  const handleSubmitWithDebug = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Run debug first
    await debugProfilesTable();
    
    // Then proceed with normal submission
    await handleSubmit(e);
  };

  // Fetch users for filter
  useEffect(() => {
    if (showAuditLogs) {
      (async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email');
        if (!error) setAuditUsers(data || []);
      })();
    }
  }, [showAuditLogs]);

  // Fetch audit logs
  useEffect(() => {
    if (showAuditLogs) {
      setAuditLoading(true);
      setAuditError('');
      (async () => {
        const { data, error } = await supabase
          .from('audit_logs')
          .select('*, profiles:user_id(id, first_name, last_name, email)')
          .order('created_at', { ascending: false });
        if (error) {
          setAuditError('Failed to load audit logs');
          setAuditLogs([]);
        } else {
          setAuditLogs(data || []);
        }
        setAuditLoading(false);
      })();
    }
  }, [showAuditLogs]);

  // Filter audit logs
  useEffect(() => {
    let filtered = auditLogs;
    if (auditUserFilter.length > 0) {
      const ids = auditUserFilter.map(u => u.value);
      filtered = filtered.filter(log => ids.includes(log.user_id));
    }
    if (auditActionFilter.length > 0) {
      // Get all selected action values (including if a category is selected)
      let selectedActions = [];
      auditActionFilter.forEach(option => {
        if (option.__isCategory) {
          // Add all actions in this category
          selectedActions.push(...getActionsForCategory(option.label).map(a => a.value));
        } else {
          selectedActions.push(option.value);
        }
      });
      // Remove duplicates
      selectedActions = [...new Set(selectedActions)];
      filtered = filtered.filter(log => selectedActions.includes(log.action));
    }
    if (auditDateRange.from) {
      filtered = filtered.filter(log => log.created_at >= auditDateRange.from);
    }
    if (auditDateRange.to) {
      const toDate = auditDateRange.to + 'T23:59:59';
      filtered = filtered.filter(log => log.created_at <= toDate);
    }
    setAuditFilteredLogs(filtered);
  }, [auditLogs, auditUserFilter, auditActionFilter, auditDateRange]);

  function handleExportAuditPDF() {
    const doc = new jsPDF({ orientation: 'landscape' });

    // Add logos (side by side)
    const logo1 = `${window.location.origin}/GabaySakunaLogo.png`;
    const logo2 = `${window.location.origin}/LegzapiCDRRMO.png`;
    doc.addImage(logo1, 'PNG', 15, 8, 24, 24);
    doc.addImage(logo2, 'PNG', 265, 8, 24, 24);

    // Main title: GabaySakuna
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Audit Logs', 148, 20, { align: 'center' });

    // Subtitle: GabaySakuna Audit Logs
    doc.setFontSize(15);
    doc.setFont('helvetica', 'normal');
    doc.text('GabaySakuna Audit Logs', 148, 30, { align: 'center' });

    // Add date and time printed
    const now = new Date();
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Printed: ${now.toLocaleString()}`, 148, 38, { align: 'center' });

    autoTable(doc, {
      head: [['Date', 'User', 'Action', 'Target', 'Details']],
      body: auditFilteredLogs.map(log => [
        new Date(log.created_at).toLocaleString(),
        log.profiles ? `${log.profiles.first_name} ${log.profiles.last_name}` : log.user_id,
        log.action,
        log.target_id || '',
        renderAuditLogDetailsText(log.details)
      ]),
      startY: 46,
      headStyles: { fillColor: [58, 162, 219], textColor: 255, fontStyle: 'bold' },
      styles: { font: 'helvetica', fontSize: 11, cellPadding: 3, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 38 }, // Date
        1: { cellWidth: 48 }, // User
        2: { cellWidth: 32 }, // Action
        3: { cellWidth: 60 }, // Target
        // 4: Details column: no width, let it auto-wrap
      },
      didDrawPage: function (data) {
        // Footer: Requested by
        if (currentUser && currentUser.first_name && currentUser.last_name) {
          doc.setFontSize(10);
          doc.text(`Requested by: ${currentUser.first_name} ${currentUser.last_name}`,
            15, doc.internal.pageSize.getHeight() - 10);
        }
      }
    });
    doc.save('AuditLogs.pdf');
  }

  // Helper to render details as plain text for PDF
  function renderAuditLogDetailsText(details) {
    if (!details) return '';
    if (typeof details === 'string') return details;
    if (typeof details === 'object') {
      const keys = Object.keys(details);
      if (keys.length === 0) return '';
      if (keys.length === 1) {
        return `${keys[0]}: ${details[keys[0]]}`;
      }
      // Otherwise, join as bullet-style lines
      return keys.map(key => `• ${key}: ${details[key]}`).join('\n');
    }
    return '';
  }

  if (!currentUser) return <Preloader text="Loading..." />;
  if (currentUser.role !== 'super admin') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }
  if (error) return <div>{error}</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8'}}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Manage Users</div>
          </div>
          <ProfileMenu />
        </div>
        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <div className="search-actions-row">
          <div className="search-filters">
            <input
              type="text"
              placeholder="Search users"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
            />
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="type-filter"
            >
              <option value="">All Roles</option>
              <option value="super admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="barangay">Barangay</option>
              <option value="resident">Resident</option>
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="type-filter"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={() => setShowAuditLogs(true)}
              className="summary-button"
            >
              Printable Audit Logs
            </button>
          </div>
          <div className="action-buttons">
            <button onClick={() => openForm()} className="create-button">
              <FaPlus /> Add User
            </button>
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>Avatar</th>
                <th style={{ padding: 12 }}>Profile</th>
                <th style={{ padding: 12 }}>Email</th>
                <th style={{ padding: 12 }}>Mobile</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Role</th>
                <th style={{ padding: 12 }}>ID Status</th>
                <th style={{ padding: 12 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>
                      <img 
                        src={user.avatar_url || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg'} 
                        alt="User avatar"
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    </td>
                    <td style={{ ...fadeCellStyle, padding: 12 }}>
                      <div style={{ fontWeight: 'bold' }}>{user.first_name} {user.last_name}</div>
                      <div style={{ color: '#444', fontSize: 14 }}>{user.house_street}, {user.subdivision}</div>
                    </td>
                    <td style={{ ...fadeCellStyle, padding: 12 }}>{user.email}</td>
                    <td style={{ ...fadeCellStyle, padding: 12 }}>{user.mobile_number}</td>
                    <td style={{ padding: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 14,
                        background: user.status === 'active' ? '#e6f4ea' : 
                                   user.status === 'inactive' ? '#fce8e6' : 
                                   user.status === 'archived' ? '#ffebee' : '#f1f3f4',
                        color: user.status === 'active' ? '#1e7e34' : 
                               user.status === 'inactive' ? '#d32f2f' : 
                               user.status === 'archived' ? '#c62828' : '#5f6368',
                        border: '1px solid #eee',
                        minWidth: 70,
                        textAlign: 'center'
                      }}>{user.status.charAt(0).toUpperCase() + user.status.slice(1)}</span>
                    </td>
                    <td style={{ padding: 12 }}>{user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}</td>
                    <td style={{ padding: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 14,
                        background: user.id_verification_status === 'verified' ? '#e6f4ea' : 
                                   user.id_verification_status === 'submitted' ? '#fff3cd' : 
                                   user.id_verification_status === 'rejected' ? '#ffebee' : 
                                   user.id_verification_status === 'pending' ? '#f1f3f4' : '#f1f3f4',
                        color: user.id_verification_status === 'verified' ? '#1e7e34' : 
                               user.id_verification_status === 'submitted' ? '#856404' : 
                               user.id_verification_status === 'rejected' ? '#c62828' : 
                               user.id_verification_status === 'pending' ? '#5f6368' : '#5f6368',
                        border: '1px solid #eee',
                        minWidth: 70,
                        textAlign: 'center'
                      }}>{user.id_verification_status ? user.id_verification_status.charAt(0).toUpperCase() + user.id_verification_status.slice(1) : 'Pending'}</span>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => openForm(user)} style={{ background: '#fbc02d', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', marginLeft: 4, cursor: 'pointer' }}>
                          Edit
                        </button>
                        <button onClick={() => handleArchive(user.id)} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', marginLeft: 4, cursor: 'pointer' }}>
                          Archive
                        </button>
                        {isUserBanned(user.id) ? (
                          <button onClick={() => openUnbanMenu(user.id)} style={{ background: '#28a745', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', marginLeft: 4, cursor: 'pointer' }}>
                            Unban
                          </button>
                        ) : (
                          <button onClick={() => openBanMenu(user.id)} style={{ background: '#dc3545', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', marginLeft: 4, cursor: 'pointer' }}>
                            Ban
                          </button>
                        )}
                      </div>
                      {isUserBanned(user.id) && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#dc3545' }}>
                          <strong>Banned:</strong> {getBanInfo(user.id)?.reason}
                          <br />
                          <strong>Time left:</strong> {getBanInfo(user.id)?.timeLeft}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setPage(i + 1)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    background: page === i + 1 ? '#08b1c7' : '#fff',
                    color: page === i + 1 ? '#fff' : '#333',
                    fontWeight: page === i + 1 ? 'bold' : 'normal'
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
          </div>
        </div>
        {showForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 24,
                width: '90%',
                maxWidth: 600,
                height: 700,
                boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 24,
                  flexShrink: 0
                }}
              >
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>{editId ? 'Edit' : 'Add'} User</h2>
                <button
                  onClick={() => setShowForm(false)}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#888'
                  }}
                >
                  ×
                </button>
              </div>
              <form
                onSubmit={handleSubmitWithDebug}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <img 
                    src={form.avatar_url || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg'} 
                    alt="Avatar preview"
                    style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', marginBottom: 16, border: '2px solid #ccc' }}
                  />
                  <div>
                    <label htmlFor="avatar-upload" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 15px',
                      background: '#3b82f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: '#fff',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                    >
                      <FaUpload />
                      <span>Upload Image</span>
                    </label>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>User ID Image</label>
                  {form.id_document_url ? (
                    <img
                      src={form.id_document_url}
                      alt="User ID Preview"
                      style={{ width: 120, height: 80, objectFit: 'contain', borderRadius: 8, border: '1px solid #ccc', marginBottom: 8 }}
                    />
                  ) : null}
                  <div>
                    <label htmlFor="id-image-upload" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 15px',
                      background: '#3b82f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      color: '#fff',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}
                    >
                      <FaUpload />
                      <span>Upload ID Image</span>
                    </label>
                    <input
                      id="id-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleIdImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>First Name</label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Last Name</label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={form.last_name}
                    onChange={e => setForm({ ...form, last_name: e.target.value })}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Email</label>
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Mobile Number</label>
                  <input
                    type="tel"
                    placeholder="Mobile Number"
                    value={form.mobile_number}
                    onChange={e => setForm({ ...form, mobile_number: e.target.value })}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Birth Date</label>
                  <input
                    type="date"
                    placeholder="Birth Date"
                    value={form.birth_date}
                    onChange={e => setForm({ ...form, birth_date: e.target.value })}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Gender</label>
                  <select
                    value={form.gender}
                    onChange={e => setForm({ ...form, gender: e.target.value })}
                    required
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Address Information</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500 }}>House/Street</label>
                      <input
                        type="text"
                        placeholder="House/Street"
                        value={form.house_street}
                        onChange={e => setForm({ ...form, house_street: e.target.value })}
                        required
                        style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500 }}>Subdivision (Optional)</label>
                      <input
                        type="text"
                        placeholder="Subdivision (Optional)"
                        value={form.subdivision}
                        onChange={e => setForm({ ...form, subdivision: e.target.value })}
                        style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500 }}>Barangay</label>
                      <select
                        value={form.barangay}
                        onChange={e => setForm({ ...form, barangay: e.target.value })}
                        required
                        style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                      >
                        <option value="">Select Barangay</option>
                        {barangaysData.map(barangay => (
                          <option key={barangay.number} value={barangay.name}>{barangay.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500 }}>City</label>
                      <input
                        type="text"
                        placeholder="City"
                        value={form.city}
                        onChange={e => setForm({ ...form, city: e.target.value })}
                        required
                        disabled
                        style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 16, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500 }}>Province</label>
                      <input
                        type="text"
                        placeholder="Province"
                        value={form.province}
                        onChange={e => setForm({ ...form, province: e.target.value })}
                        required
                        disabled
                        style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 16, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 0 }}>
                      <label style={{ fontWeight: 500 }}>ZIP Code</label>
                      <input
                        type="text"
                        placeholder="ZIP Code"
                        value={form.zip}
                        onChange={e => setForm({ ...form, zip: e.target.value })}
                        required
                        disabled
                        style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', background: '#f5f5f5', fontSize: 16, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontWeight: 500 }}>Status</label>
                    <select
                      value={form.status}
                      onChange={e => setForm({ ...form, status: e.target.value })}
                      required
                      style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box' }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Role:</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    disabled={currentUser.role?.trim() !== 'super admin'}
                    required
                    style={{ 
                      width: '100%',
                      padding: 12, 
                      borderRadius: 6, 
                      border: '1px solid #ccc', 
                      fontSize: 16,
                      boxSizing: 'border-box',
                      backgroundColor: currentUser.role?.trim() !== 'super admin' ? '#f5f5f5' : 'white',
                      color: currentUser.role?.trim() !== 'super admin' ? '#666' : 'black'
                    }}
                  >
                    <option value="resident">Resident</option>
                    <option value="barangay">Barangay</option>
                    <option value="admin">Admin</option>
                    <option value="super admin">Super Admin</option>
                  </select>
                  {currentUser.role?.trim() !== 'super admin' && (
                    <small style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Only Super Admin can change user roles
                    </small>
                  )}
                </div>
              </form>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginTop: 24,
                  flexShrink: 0,
                  background: '#fff'
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    background: '#fff',
                    color: '#333',
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="manage-users-form"
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: 'none',
                    background: loading ? '#ccc' : '#19c37d',
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => document.querySelector('form').requestSubmit()}
                >
                  {loading ? 'Saving...' : (editId ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Ban Menu Popup */}
        {showBanMenu && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: 32, 
              minWidth: 400,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ marginBottom: 24 }}>Ban User</h2>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: '500' }}>Ban Duration</label>
                <select
                  value={banForm.duration}
                  onChange={e => setBanForm({ ...banForm, duration: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                >
                  <option value="1h">1 Hour</option>
                  <option value="24h">24 Hours</option>
                  <option value="7d">7 Days</option>
                  <option value="permanent">Permanent (100 years)</option>
                  <option value="custom">Custom Duration</option>
                </select>
              </div>

              {banForm.duration === 'custom' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: '500' }}>Hours</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={banForm.customHours}
                      onChange={e => setBanForm({ ...banForm, customHours: e.target.value })}
                      min="0"
                      style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: '500' }}>Minutes</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={banForm.customMinutes}
                      onChange={e => setBanForm({ ...banForm, customMinutes: e.target.value })}
                      min="0"
                      max="59"
                      style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                    />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: '500' }}>Reason for Ban</label>
                <textarea
                  placeholder="Enter reason for banning this user..."
                  value={banForm.reason}
                  onChange={e => setBanForm({ ...banForm, reason: e.target.value })}
                  required
                  rows="4"
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowBanMenu(false)}
                  style={{ background: '#eee', border: 'none', borderRadius: 8, padding: '12px 24px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleBanUser}
                  style={{ 
                    background: '#dc3545', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 8, 
                    padding: '12px 24px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Ban User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unban Menu Popup */}
        {showUnbanMenu && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: 32, 
              minWidth: 400,
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ marginBottom: 24 }}>Unban User</h2>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: '500' }}>Unban Action</label>
                <select
                  value={banForm.duration}
                  onChange={e => setBanForm({ ...banForm, duration: e.target.value })}
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                >
                  <option value="immediate">Unban Immediately</option>
                  <option value="1h">Reduce to 1 Hour</option>
                  <option value="24h">Reduce to 24 Hours</option>
                  <option value="7d">Reduce to 7 Days</option>
                  <option value="custom">Custom Duration</option>
                </select>
              </div>

              {banForm.duration === 'custom' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: '500' }}>Hours</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={banForm.customHours}
                      onChange={e => setBanForm({ ...banForm, customHours: e.target.value })}
                      min="0"
                      style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: '500' }}>Minutes</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={banForm.customMinutes}
                      onChange={e => setBanForm({ ...banForm, customMinutes: e.target.value })}
                      min="0"
                      max="59"
                      style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%' }}
                    />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: '500' }}>Reason for Unban (Optional)</label>
                <textarea
                  placeholder="Enter reason for unbanning this user..."
                  value={banForm.reason}
                  onChange={e => setBanForm({ ...banForm, reason: e.target.value })}
                  rows="4"
                  style={{ padding: 10, borderRadius: 6, border: '1px solid #ccc', width: '100%', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button 
                  onClick={() => setShowUnbanMenu(false)}
                  style={{ background: '#eee', border: 'none', borderRadius: 8, padding: '12px 24px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUnbanUser}
                  style={{ 
                    background: '#28a745', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 8, 
                    padding: '12px 24px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Unban User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showAuditLogs && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 0, minWidth: 320, maxWidth: 950, width: '98%', height: 700,
            boxShadow: '0 8px 32px rgba(8, 177, 199, 0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative'
          }}>
            {/* Header */}
            <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 10, padding: '32px 48px 16px 48px', borderTopLeftRadius: 16, borderTopRightRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 60 }}>
              <h2 style={{ fontWeight: 800, fontSize: 26, color: '#000', margin: 0 }}>Audit Logs</h2>
              <button
                onClick={() => setShowAuditLogs(false)}
                style={{ background: 'none', border: 'none', fontSize: 32, color: '#888', cursor: 'pointer', marginLeft: 16 }}
                aria-label="Close"
              >×</button>
            </div>
            {/* Filters: each on its own row */}
            <div style={{ padding: '24px 48px 0 48px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ minWidth: 220 }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#222', marginBottom: 4, marginLeft: 2 }}>User(s)</label>
                <Select
                  options={auditUsers.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.email})` }))}
                  value={auditUserFilter}
                  onChange={setAuditUserFilter}
                  isSearchable
                  isMulti
                  placeholder="Select or search user(s)..."
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    container: base => ({ ...base, width: '100%', marginBottom: 0 }),
                    control: base => ({ ...base, width: '100%', minHeight: 44 }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    menu: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>
              <div style={{ minWidth: 220 }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#222', marginBottom: 4 }}>Action(s)</label>
                <Select
                  options={[
                    ...ACTION_CATEGORIES.map(cat => ({
                      label: cat.label,
                      options: cat.options,
                      __isCategory: true,
                    })),
                  ]}
                  value={auditActionFilter}
                  onChange={selected => {
                    // If a category is selected, add a special object to the filter
                    // react-select passes group headers as null, so we need to handle category selection manually
                    // We'll use the onMenuClose event to detect if a category was clicked
                    setAuditActionFilter(selected || []);
                  }}
                  isSearchable
                  isMulti
                  placeholder="Select or search action(s)..."
                  closeMenuOnSelect={false}
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    container: base => ({ ...base, width: '100%', marginBottom: 0 }),
                    control: base => ({ ...base, width: '100%', minHeight: 44 }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    menu: base => ({ ...base, zIndex: 9999 })
                  }}
                  formatGroupLabel={group => (
                    <div style={{ fontWeight: 700, color: '#08b1c7', fontSize: 15 }}>{group.label}</div>
                  )}
                />
              </div>
              <div style={{ minWidth: 260 }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#222', marginBottom: 4 }}>Date Range</label>
                <div style={{ display: 'flex', gap: 16, width: '100%' }}>
                  <input
                    type="date"
                    value={auditDateRange.from}
                    onChange={e => setAuditDateRange(r => ({ ...r, from: e.target.value }))}
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box', background: '#fff', marginBottom: 0 }}
                  />
                  <input
                    type="date"
                    value={auditDateRange.to}
                    onChange={e => setAuditDateRange(r => ({ ...r, to: e.target.value }))}
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16, boxSizing: 'border-box', background: '#fff', marginBottom: 0 }}
                  />
                </div>
              </div>
              <button
                onClick={handleExportAuditPDF}
                style={{ background: '#19c37d', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 16, fontWeight: 700, boxShadow: '0 2px 8px #0001', marginLeft: 'auto', marginBottom: 8 }}
              >Export as PDF</button>
            </div>
            {/* Table */}
            <div style={{ minHeight: 120, background: '#f8f8f8', borderRadius: 10, padding: 28, color: '#333', margin: 32, overflowX: 'auto', overflowY: 'auto', boxShadow: '0 2px 8px #0001', maxHeight: 320 }}>
              {auditLoading ? (
                <Preloader text="Loading audit logs..." />
              ) : auditError ? (
                <div style={{ color: '#d32f2f' }}>{auditError}</div>
              ) : auditFilteredLogs.length === 0 ? (
                <div>No results found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left', background: '#eaf6fb', height: 48 }}>
                      <th style={{ padding: '14px 16px' }}>Date</th>
                      <th style={{ padding: '14px 16px' }}>User</th>
                      <th style={{ padding: '14px 16px' }}>Action</th>
                      <th style={{ padding: '14px 16px' }}>Target</th>
                      <th style={{ padding: '14px 16px' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditFilteredLogs.map((log, idx) => (
                      <tr key={log.id || idx} style={{ borderBottom: '1px solid #eee', background: idx % 2 === 0 ? '#fff' : '#f4f8f8', height: 44 }}>
                        <td style={{ padding: '12px 16px' }}>{new Date(log.created_at).toLocaleString()}</td>
                        <td style={{ padding: '12px 16px' }}>{log.profiles ? `${log.profiles.first_name} ${log.profiles.last_name}` : log.user_id}</td>
                        <td style={{ padding: '12px 16px' }}>{log.action}</td>
                        <td style={{ padding: '12px 16px' }}>{log.target_id || ''}</td>
                        <td style={{ padding: '12px 16px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{renderAuditLogDetails(log.details)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsers;

// Helper to render details as sentence or bullets
function renderAuditLogDetails(details) {
  if (!details) return '';
  if (typeof details === 'string') return details;
  if (typeof details === 'object') {
    const keys = Object.keys(details);
    if (keys.length === 0) return '';
    // If only one key, render as sentence
    if (keys.length === 1) {
      const value = details[keys[0]];
      return `${keys[0]}: ${typeof value === 'object' ? JSON.stringify(value) : value}`;
    }
    // Otherwise, render as bullets
    return (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {keys.map(key => {
          const value = details[key];
          return (
            <li key={key}>
              <b>{key}:</b> {typeof value === 'object' ? JSON.stringify(value) : value}
            </li>
          );
        })}
      </ul>
    );
  }
  return '';
}